import { css, CSSResultGroup, html, LitElement } from "lit";
import { customElement, state } from "lit/decorators.js";

interface InputEventDetails {
	value: string;
	name: string;
	label: string;
	additionalIdentifier?: string;
}

interface BuzzerMessage {
	type: 'team_joined' | 'team_buzzed' | 'lock_buzzers' | 'reset_buzzers';
	teamName?: string;
	timestamp?: number;
	locked?: boolean;
}

@customElement("team-buzzer")
export class TeamBuzzer extends LitElement {
	static readonly styles: CSSResultGroup = css`
        :host {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: 100vh;
            width: 100%;
            gap: 20px;
        }

        .setup-form {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 16px;
        }

        .buzzer-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 20px;
        }

        .team-name {
            font-size: 1.5rem;
            font-weight: bold;
            color: #333;
        }

        .buzzer-button {
            background-color: var(--brightBlueColor, #348df5);
            border: none;
            border-radius: 50%;
            color: #fff;
            font-size: 2rem;
            font-weight: 700;
            height: 90vh;
            width: 90vw;
            max-width: 500px;
            max-height: 500px;
            cursor: pointer;
            user-select: none;
            -webkit-user-select: none;
            transition: all 0.1s ease;
        }

        .buzzer-button:hover:not(:disabled) {
            background-color: #2a7ae0;
        }

        .buzzer-button:active:not(:disabled),
        .buzzer-button.pressed {
            background-color: #ff4444;
            transform: scale(0.95);
        }

        .buzzer-button:disabled {
            background-color: #6c757d;
            cursor: not-allowed;
        }
    `;

	@state() private teamName = '';
	@state() private isSetup = false;
	@state() private isPressed = false;
	@state() private isLocked = false;
	@state() private buzzersGloballyLocked = false;
	private webSocket: WebSocket | null = null;

	connectedCallback() {
		super.connectedCallback();
		this.addEventListener('input-change', this._handleInputChange);
		this.initWebSocket();
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		if (this.webSocket) {
			this.webSocket.close();
		}
	}

	private initWebSocket() {
		const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
		const wsUrl = `${protocol}//${window.location.host}/ws/buzzer/`;

		this.webSocket = new WebSocket(wsUrl);

		this.webSocket.onopen = () => {
			console.log('Team buzzer WebSocket connected');
		};

		this.webSocket.onmessage = (event) => {
			const message: BuzzerMessage = JSON.parse(event.data);
			this.handleWebSocketMessage(message);
		};

		this.webSocket.onclose = () => {
			console.log('Team buzzer WebSocket closed, reconnecting in 3 seconds...');
			setTimeout(() => this.initWebSocket(), 3000);
		};
	}

	private handleWebSocketMessage(message: BuzzerMessage) {
		switch (message.type) {
			case 'lock_buzzers':
				this.buzzersGloballyLocked = message.locked || false;
				break;
			case 'reset_buzzers':
				this.isPressed = false;
				this.isLocked = false;
				this.buzzersGloballyLocked = false;
				break;
		}
	}

	private sendWebSocketMessage(message: BuzzerMessage) {
		if (this.webSocket && this.webSocket.readyState === WebSocket.OPEN) {
			this.webSocket.send(JSON.stringify(message));
		}
	}

	private _handleInputChange = (event: CustomEvent<InputEventDetails>) => {
		if (event.detail.name === 'teamName') {
			this.teamName = event.detail.value;
		}
	};

	private handleConfirm() {
		if (this.teamName.trim()) {
			this.isSetup = true;
			this.sendWebSocketMessage({
				type: 'team_joined',
				teamName: this.teamName.trim()
			});
		}
	}

	private handleBuzzerPress() {
		if (this.isLocked || this.buzzersGloballyLocked) return;

		this.isPressed = true;
		this.isLocked = true;

		this.sendWebSocketMessage({
			type: 'team_buzzed',
			teamName: this.teamName,
			timestamp: Date.now()
		});
	}

	render() {
		if (!this.isSetup) {
			return html`
                <div class="setup-form">
                    <basic-input 
                        name="teamName"
                        label="Team Name"
                        helperText="Enter your team name"
                        value="${this.teamName}">
                    </basic-input>
                    <basic-button 
                        ?disabled="${!this.teamName.trim()}"
                        @click="${this.handleConfirm}">
                        Confirm
                    </basic-button>
                </div>
            `;
		}

		return html`
            <div class="buzzer-container">
                <div class="team-name">${this.teamName}</div>
                <button 
                    class="buzzer-button ${this.isPressed ? 'pressed' : ''}"
                    ?disabled="${this.isLocked || this.buzzersGloballyLocked}"
                    @click="${this.handleBuzzerPress}">
                    ${this.isPressed ? 'BUZZED!' : 'BUZZ!'}
                </button>
            </div>
        `;
	}
}

export default TeamBuzzer;